import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';

// Theme
import theme from './theme';

// Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import RoleSwitcher from './components/RoleSwitcher';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Verify from './pages/Verify';
import Onboarding from './pages/Onboarding';
import SimpleDashboard from './pages/SimpleDashboard';
import PatientDashboard from './pages/PatientDashboard';
import DoctorDashboard from './pages/DoctorDashboard';
import FamilyMemberDashboard from './pages/FamilyMemberDashboard';
import HospitalEmergencyDashboard from './pages/HospitalEmergencyDashboard';
import AmbulanceDashboard from './pages/AmbulanceDashboard';
import AdminDashboard from './pages/AdminDashboard';
import EmergencyAlerts from './pages/EmergencyAlerts';
import Users from './pages/Users';
import SystemMonitor from './pages/SystemMonitor';
import NotFound from './pages/NotFound';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  // Get user role from localStorage, default to patient for registered users
  const getUserRole = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.role || 'patient';
      }
      return 'admin'; // Default for non-authenticated users
    } catch {
      return 'admin';
    }
  };

  const [currentRole, setCurrentRole] = React.useState(getUserRole());

  const getDashboardComponent = () => {
    switch (currentRole) {
      case 'patient': return <PatientDashboard />;
      case 'doctor': return <DoctorDashboard />;
      case 'admin': return <AdminDashboard />;
      case 'family_caregiver': return <FamilyMemberDashboard memberRole="caregiver" />;
      case 'family_viewer': return <FamilyMemberDashboard memberRole="viewer" />;
      case 'family_emergency': return <FamilyMemberDashboard memberRole="emergency_contact" />;
      case 'hospital': return <HospitalEmergencyDashboard />;
      case 'ambulance': return <AmbulanceDashboard />;
      default: return <SimpleDashboard />;
    }
  };
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <div>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            autoHideDuration={5000}
          >
            <CssBaseline />
            <Router>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify" element={<Verify />} />
                <Route path="/onboarding" element={<Onboarding />} />

                {/* Private routes */}
                <Route element={<PrivateRoute />}>
                  <Route element={<Layout />}>
                    <Route path="/" element={
                      <div>
                        <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0', marginBottom: '16px' }}>
                          <RoleSwitcher currentRole={currentRole} onRoleChange={setCurrentRole} />
                        </div>
                        {getDashboardComponent()}
                      </div>
                    } />
                    <Route path="/dashboard" element={<Navigate to="/" replace />} />
                    <Route path="/emergencies" element={<EmergencyAlerts />} />
                    <Route path="/emergencies/:id" element={<EmergencyAlerts />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/users/:id" element={<Users />} />
                    <Route path="/health-records" element={<HealthRecords />} />
                    <Route path="/health-records/:userId" element={<HealthRecords />} />
                    <Route path="/monitor" element={<SystemMonitor />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/profile" element={<Profile />} />
                  </Route>
                </Route>

                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Router>
          </SnackbarProvider>
        </div>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;

// Placeholder components for routes that need to be created
const HealthRecords = () => <div>Health Records - To be implemented</div>;
const Reports = () => <div>Reports - To be implemented</div>;
const Settings = () => <div>Settings - To be implemented</div>;
const Profile = () => <div>Profile - To be implemented</div>;
